import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Home } from './index';
import dayjs from 'dayjs';
import useCompanyCustDep from '../../hooks/useCompanyCustDep';
import { setCustCd } from '../../Reducer/SearchReducer';
import {
  useDelete1,
  useSave1,
  useSave2,
  useSave3,
  useSave4,
  useSearch1,
  useSearch2,
  useSearch5,
  useSearch6,
} from './Home_QUERY';
import { useSave } from '../../hooks/useSaveN';

function HomeFunction() {
  //#region 변수 및 상태
  const dispatch = useDispatch();
  const { userInfo } = useSelector((state) => state.BaseInfo);
  const searchInfo = useSelector((state) => state.SearchInfo);

  const [noticeData, setNoticeData] = useState([]);
  const [newsData, setNewsData] = useState([]);
  const [seriousData, setSeriosData] = useState([]);
  const [workListData, setWorkListData] = useState([]);
  const [completeWorkListData, setCompleteWorkListData] = useState([]);
  const [alarmData, setAlarmData] = useState([]);
  const [alarmDetailData, setAlarmDetailData] = useState([]);
  const [myCalendars, setMyCalendars] = useState([]);
  const [calendarEvents, setCalendarEvents] = useState([]);

  const [selectedCalendarDate, setSelectedCalendarDate] = useState(dayjs());
  const [calendarViewDate, setCalendarViewDate] = useState(dayjs());

  const WorkRowsRef = useRef([]);

  const headerInputs = useMemo(
    () => ({
      authCd: userInfo?.authCd,
      siteCd: '0',
      yearValue: dayjs().format('YYYY-MM-DD'),
    }),
    [userInfo?.authCd]
  );

  const { handleCoChange, handleCustChange } = useCompanyCustDep({
    requireCompany: true,
    requireCust: true,
    noDefaultCompanyOnInit: userInfo?.authCd === 'ADMIN',
  });
  //#endregion

  //#region 조회 로직
  // 메인 데이터 조회 (알람, 업무리스트, 뉴스 등)
  const { refetch: Search1 } = useSearch1({
    onSuccess: (rows) => {
      if (rows && rows[0]) {
        setAlarmData(rows[0].alarmInfo || []);
        setWorkListData(sortWorkList(rows[0].workListInfo || []));
        setNewsData(rows[0].safetyNewsInfo || []);
        setNoticeData(rows[0].noticeInfo || []);

        if (rows[0].seriousDisasterInfo?.length) {
          const changeData = rows[0].seriousDisasterInfo
            .filter((item) => item.codeNm !== '가점 사항')
            .map((item) => ({
              subject: `${item.codeNm}(${parseInt(item.value, 10) || 0})`,
              value: item.value,
              fullMark: 100,
              level: item.level,
            }));
          setSeriosData(changeData);
        } else {
          setSeriosData([]);
        }
      }
    },
  });

  const { run: search2 } = useSearch2({
    onSuccess: (rows) => setAlarmDetailData(rows),
  });

  const { run: search6 } = useSearch6({
    onSuccess: (rows) => setCompleteWorkListData(rows),
  });

  // 캘린더 이벤트 조회 (달력 시점 변경 시 호출)
  const { run: Search5 } = useSearch5({
    onSuccess: (rows) => {
      const calendarColorMap = new Map();
      myCalendars.forEach((cal) => {
        if (cal.CalId && cal.Color && cal.Checked) {
          calendarColorMap.set(String(cal.CalId), cal.Color);
        }
      });

      const mapped = rows.map((e) => ({
        id: `${e.calEId}_${e.startDt}`,
        title: e.title || '',
        startDt: e.startDt,
        endDt: e.endDt,
        allDay: e.allDay || 'N',
        // 서버에서 주는 색상(displayColor)이 있으면 우선 사용, 없으면 맵핑 색상 사용
        color:
          e.displayColor || calendarColorMap.get(String(e.calId)) || '#1976d2',
        location: e.location || '',
        description: e.description || '',
      }));
      setCalendarEvents(mapped);
    },
  });

  // 초기 고객사 코드 세팅(ADMIN은 회사·사업장 선택 후에만 cust 반영)
  useEffect(() => {
    if (userInfo?.authCd === 'ADMIN') return;
    if (!searchInfo.custCd && userInfo?.custCd) {
      dispatch(setCustCd(userInfo.custCd));
    }
  }, [userInfo, searchInfo.custCd, dispatch]);

  // 캘린더 데이터 갱신: 달력의 월(viewDate)이 바뀌거나 캘린더 체크박스가 바뀔 때 자동 호출
  useEffect(() => {
    Search5({ currentDate: calendarViewDate, userInfo });
  }, [calendarViewDate, myCalendars]);

  // 메인 데이터 조회 실행
  useEffect(() => {
    Search1();
  }, []);
  //#endregion

  //#region 저장/수정/삭제 로직 (Direct Async Call)
  const { runAsync: Save1Async } = useSave1();
  const { runAsync: Save2Async } = useSave2();
  const { runAsync: Save3Async } = useSave3();
  const { runAsync: Save4Async } = useSave4();
  const { runAsync: Delete1Async } = useDelete1();

  // 업무 전체 저장/수정 (로딩바 필요시 useSave 사용 유지)
  const { run: SaveLogic1 } = useSave({
    menuId: 'HOME_WORK_U1',
    getChangedRows: () => WorkRowsRef.current,
    requestSave: (rows) => Save1Async({ rows }),
    refresh: () => Search1(),
  });

  //#endregion

  //#region 핸들러 함수

  // 캘린더 월 변경 핸들러 (MiniCalendar 화살표 클릭 시 실행됨)
  const handleMonthChange = (date) => {
    setCalendarViewDate(dayjs(date));
  };

  const handlePinWork = async (workId, nextPinYn, currentPriority) => {
    const rows = [{ workId, pinYn: nextPinYn, priorityBit: currentPriority }];
    await Save3Async({ rows });
    Search1();
  };

  const handlePriorityChange = async (workId, priorityBit) => {
    const targetTask = workListData.find(
      (item) => String(item.workId) === String(workId)
    );
    const currentPin = targetTask?.pinYn || 'N';
    const rows = [{ workId, priorityBit, pinYn: currentPin }];
    await Save3Async({ rows });
    Search1();
  };

  const handleStatusChange = async (workId, statusCd) => {
    await Save2Async({ rows: [{ workId, statusCd }] });
    Search1();
  };

  const handleSaveWork = (formData, workId = '') => {
    WorkRowsRef.current = [
      {
        workId,
        custCd: searchInfo.custCd || userInfo.custCd || '',
        ...formData,
      },
    ];
    SaveLogic1();
  };

  const handleDeleteWork = async (workId) => {
    await Delete1Async({ rows: [{ workId }] });
    Search1();
    // 완료 업무 전체보기 팝업(CompleteWorkListDialog) 데이터는 search6 전용 — 삭제 후 동일 재조회
    search6();
  };

  const handleSaveLogic4 = async (item) => {
    const rows = [
      {
        gubun: '1',
        alarmCd: String(item.alarmCd || ''),
        userId: userInfo.userid,
      },
    ];
    try {
      await Save4Async({ rows });
      search2();
      Search1();
    } catch (error) {
      console.error('알람 읽음 처리 실패:', error);
    }
  };

  const handleReadAll = async () => {
    if (!alarmDetailData.length) return;
    const rows = [{ gubun: '2', alarmCd: '', userId: userInfo.userid }];
    try {
      await Save4Async({ rows });
      search2();
      Search1();
    } catch (error) {
      console.error('알람 전체 읽음 처리 실패:', error);
    }
  };

  const sortWorkList = (list) => {
    return [...list].sort((a, b) => {
      if (a.pinYn === 'Y' && b.pinYn !== 'Y') return -1;
      if (a.pinYn !== 'Y' && b.pinYn === 'Y') return 1;
      return (a.priorityBit ?? 2) - (b.priorityBit ?? 2);
    });
  };

  /** Header: 회사/사업장 변경 후 알람 상세 재조회 */
  const SearchLogic3 = useCallback(async () => {
    search2();
  }, [search2]);

  /** Header: 회사/사업장 변경 후 완료업무·캘린더 일정 재조회 */
  const SearchLogic4 = useCallback(async () => {
    search6();
    Search5({ currentDate: calendarViewDate, userInfo });
  }, [search6, Search5, calendarViewDate, userInfo]);
  //#endregion

  return (
    <div className="wrap">
      <Home
        noticeData={noticeData}
        headerInputs={headerInputs}
        newsData={newsData}
        seriousData={seriousData}
        alarmData={alarmData}
        workListData={workListData}
        completeWorkListData={completeWorkListData}
        alarmDetailData={alarmDetailData}
        calendarEvents={calendarEvents}
        myCalendars={myCalendars}
        handleCoChange={handleCoChange}
        handleCustChange={handleCustChange}
        Search1={Search1}
        SearchLogic3={SearchLogic3}
        SearchLogic4={SearchLogic4}
        search2={search2}
        search6={search6}
        onDateChange={(date) => setSelectedCalendarDate(dayjs(date))}
        handleMonthChange={handleMonthChange}
        handleSaveLogic4={handleSaveLogic4}
        handleReadAll={handleReadAll}
        handleSaveWork={handleSaveWork}
        handleDeleteWork={handleDeleteWork}
        handleStatusChange={handleStatusChange}
        handlePriorityChange={handlePriorityChange}
        handlePinWork={handlePinWork}
      />
    </div>
  );
}

export default HomeFunction;
